/*
 * Dashboard warning banner surfacing WhatsApp templates with Meta HSM
 * problems (unmapped or non-APPROVED). Ships silent when everything is
 * healthy or the environment has never synced. Powered by
 * GET /meta-templates/health.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";

export default function MetaTemplatesWarningBanner() {
  const [health, setHealth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/meta-templates/health");
        if (!cancelled) setHealth(r.data);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!health) return null;
  // Silent when everything is fine, OR when the env has never synced
  // (avoids scaring ops on a fresh Contabo before their first sync).
  if (health.ok || !health.last_synced_at) return null;

  const unmapped = health.unmapped || [];
  const unapproved = health.unapproved || [];
  const total = health.problems_count || (unmapped.length + unapproved.length);

  return (
    <div
      className="my-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
      data-testid="meta-templates-warning-banner"
    >
      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-amber-900">
          {total} WhatsApp template{total === 1 ? "" : "s"} need attention — cadence cold sends will fail
        </div>
        <ul className="mt-1.5 text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
          {unmapped.map((t) => (
            <li key={`u-${t.id}`} data-testid={`banner-unmapped-${t.id}`}>
              • <span className="font-semibold">{t.name}</span> — no Meta template selected
            </li>
          ))}
          {unapproved.map((t) => (
            <li key={`n-${t.id}`} data-testid={`banner-unapproved-${t.id}`}>
              • <span className="font-semibold">{t.connect_name}</span> — mapped to{" "}
              <code className="font-mono">{t.meta_template_name}</code> (status: {t.meta_status})
            </li>
          ))}
        </ul>
        <div className="mt-2 flex items-center gap-3">
          <Link
            to="/templates"
            className="inline-flex items-center gap-1 text-xs font-semibold text-amber-900 hover:text-amber-950"
            data-testid="banner-go-templates"
          >
            <ArrowRight className="h-3 w-3" />
            Fix in Templates
          </Link>
          {!health.last_synced_at && (
            <span className="text-[11px] text-amber-700 inline-flex items-center gap-1">
              <RefreshCw className="h-3 w-3" /> Try clicking Sync Meta Templates first
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
